import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
	Box,
	Stack,
	IconButton,
	Typography,
	Divider,
	Tab,
	Button,
	Grid,
} from '@mui/material';

import { SyntheticEvent, useCallback, useState, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useLocation } from 'react-router-dom';

import { getControlValidationTerms } from 'common/api';
import { IFiveWResponse } from 'common/api/type-def';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';

import useTableQuery from 'hooks/useTableQuery';

import { getObligationDetails } from 'features/HorizonScanning/api';

import { getEntityData } from '../../api';
import { IEntityData } from '../../api/type-def';

import {
	countColourMapping,
	entityAssociatedData,
	getFormattedEntityValue,
} from '../mappingUtils';

import { ReactComponent as DrawerClose } from '../../../../../common/assets/images/drawer-close.svg';
import { ENTITY_TYPE } from '../../../../../common/constants/TaxonomyConstants';
import { RootState } from '../../../../../common/store/AppStore';
import { setMappingDrawer } from '../../../../../common/store/reducer/commonReducer';
import {
	setGrammarCheckCompleted,
	setSelectedTask,
	setTabIndex,
} from '../../../../../common/store/reducer/mappingDetailsReducer';

import Impact from './Impact/Impact';
import OverviewLayout from './Overview/OverviewLayout';
import Tasks from './Tasks/Tasks';

import styles from './DetailedMappingLayout.module.scss';

const { drawerCloseIconColor, focustabColor, tabTextColor, tabBorderRadius } =
	styles;

const style = {
	'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
		color: tabTextColor,
	},
	'.Mui-selected': {
		background: focustabColor,
		color: tabTextColor,
		borderRadius: tabBorderRadius,
	},
};
const { CONTROL, GROUP, OBLIGATION, CITATION } = ENTITY_TYPE;
const {
	REQUIREMENT_INVENTORY,
	RISK_REFERENCE_TAXONOMY,
	HORIZON_SCAN_AND_ALERTS,
} = MODULES;

interface IDetailMappingProps {
	moduleType: string;
	subModuleType?: string;
	readOnly?: boolean;
	isDeleteEnabled?: boolean;
}

const DetailedMappingLayout = (props: IDetailMappingProps) => {
	const {
		readOnly,
		moduleType = '',
		subModuleType = '',
		isDeleteEnabled,
	} = props;
	const disableForRegInv = moduleType !== REQUIREMENT_INVENTORY;
	const location = useLocation();

	/* Hooks */
	const { updateTableQuery } = useTableQuery({
		id: 'relationMappingTable',
	});

	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);
	const tabIndex: string = useSelector(
		(state: RootState) => state?.mappingDetails?.tabIndex,
	);
	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);

	const isImpactHidden = () => entityType !== CITATION;

	const { OK } = HTTP_STATUS_CODES;

	const { id, type, citationGroupId } = selectedEntity;

	const { state } = location;

	const entityType =
		state && state.entityType !== undefined ? state.entityType : CITATION;

	const [isLoading, setIsLoading] = useState(false);
	const showActivityTab = false;
	const [isControlValidationDataLoading, setIsControlValidationDataLoading] =
		useState(false);
	const [entityData, setEntityData] = useState<any>(null);
	const [openTask, setOpenTask] = useState<number>(0);
	const [associatedProducts, setAssociatedProducts] = useState<number>(0);
	const [openIssues, setOpenIssues] = useState<number>(0);
	const [number, setNumber] = useState('');
	const [controlValidationData, setControlValidationData] =
		useState<IFiveWResponse>();
	const [isScrollNeeded, setIsScrollNeeded] = useState<boolean>(false);
	const [isTaskCountViewEnabled, setIsTaskCountViewEnabled] = useState(false);
	const [displayId, setDisplayId] = useState('');
	const dispatch = useDispatch();
	const handleDrawerChange = useCallback(() => {
		dispatch(setMappingDrawer(!sideDrawerState));
		// to reset the tab value when ever we come out of the drawer
		dispatch(setTabIndex('overview'));
		dispatch(setGrammarCheckCompleted(false));
		updateTableQuery({ pageNumber: 0, pageSize: 10 });
	}, [sideDrawerState]);

	const handleChange = (event: SyntheticEvent, newValue: string) => {
		dispatch(setSelectedTask(''));
		dispatch(setTabIndex(newValue));
	};

	const controlQualityCheckClicked = async () => {
		setIsControlValidationDataLoading(true);
		setIsScrollNeeded(true);
		setTimeout(() => {
			setIsScrollNeeded(false);
		}, 2000);
		const response: any = await getControlValidationTerms(
			entityData?.filter((el: any) => el?.label === 'Description')[0].value,
		);
		if (response?.statusCode === OK) {
			setControlValidationData(response?.responseBody);
		} else {
			setIsControlValidationDataLoading(false);
			setControlValidationData(undefined);
		}
	};

	useEffect(() => {
		if (entityData) {
			if (entityData.filter((obj: IEntityData) => obj.label === 'number')[0]) {
				setNumber(
					entityData.filter((obj: IEntityData) => obj.label === 'number')[0]
						.value,
				);
			}
		}
	}, [entityData]);

	const setEntityDataBasedOnType = (responseBody: any) => {
		const entityVal = getFormattedEntityValue(responseBody, type);
		setEntityData(entityVal);
	};

	const fetchEntityData = async () => {
		setIsLoading(true);
		if (type?.toLowerCase() !== GROUP.toLowerCase()) {
			const response: any = await getEntityData(id, type);
			if (response?.statusCode === OK) {
				const { responseBody } = response;
				setOpenTask(
					responseBody.openTask ??
						responseBody.taskCount ??
						responseBody.openTasks,
				);
				setOpenIssues(responseBody.openIssues ?? responseBody.openIssue ?? 0);
				setAssociatedProducts(responseBody.products?.length || 0);
				setEntityDataBasedOnType(responseBody);
				setDisplayId(responseBody?.childDisplayId || '');
				setIsLoading(false);
			} else {
				setEntityData(null);
				setIsLoading(false);
			}
		} else {
			const response = await getObligationDetails(citationGroupId || id);
			const { responseBody } = response;
			setEntityDataBasedOnType(responseBody);
			setIsLoading(false);
		}
	};

	const display5Wcheck =
		tabIndex === 'overview' &&
		type === CONTROL &&
		entityType === CITATION &&
		disableForRegInv;
	useEffect(() => {
		fetchEntityData();
	}, [setEntityData, type, selectedEntity]);

	useEffect(() => {
		if (moduleType === REQUIREMENT_INVENTORY || type === OBLIGATION) {
			setIsTaskCountViewEnabled(false);
		}
	}, []);

	const displayIssueCounts = (label: string, count: number) => (
		<Stack>
			<Typography className={styles['summary-count-label']}>{label}</Typography>
			<Typography
				className={styles['summary-count']}
				style={{ color: `${countColourMapping(label)}` }}
			>
				{count}
			</Typography>
		</Stack>
	);

	return (
		<Grid container role="presentation">
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				className={styles['control-lay-text-bg']}
			>
				<Stack
					direction="row"
					justifyContent="flex-end"
					spacing={2}
					data-testid="close-button-stack"
				>
					<IconButton
						size="large"
						onClick={handleDrawerChange}
						data-testid="close-button-icon"
						className={styles['fileupload-close']}
					>
						<DrawerClose fill={drawerCloseIconColor} />
					</IconButton>
				</Stack>
				<Box className={styles['fileupload-text-box']}>
					<Stack direction="row" justifyContent="space-between">
						<Typography variant="h1" className={styles['sidedrawer-title']}>
							{number}
						</Typography>
					</Stack>
					<Typography className={styles['sidedrawer-display-id-subtitle']}>
						{`${
							selectedEntity.childDisplayId
								? selectedEntity.childDisplayId
								: displayId
						}`}
					</Typography>
					<Typography className={styles['sidedrawer-subtitle']}>
						{selectedEntity.title ||
							entityData?.filter((obj: IEntityData) => obj.label === 'name')[0]
								.value}
					</Typography>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
					>
						<Stack
							justifyContent="flex-end"
							className={
								tabIndex === 'overview'
									? styles['grammar-check-parent-stack-overview-tab']
									: styles['grammar-check-parent-stack-tasks-tab']
							}
						>
							{display5Wcheck && (
								<Button
									className={styles['control-grammar-check-button']}
									onClick={controlQualityCheckClicked}
									data-testid="control-quality-check"
								>
									Control Quality 5Ws check
								</Button>
							)}
						</Stack>
					</Stack>
				</Box>
			</Grid>
			{isTaskCountViewEnabled && (
				<Grid
					item
					xs={12}
					sm={12}
					md={12}
					lg={12}
					xl={12}
					className={styles['count-container']}
				>
					<Stack direction="row">
						{displayIssueCounts(entityAssociatedData.OPEN_ISSUES, openIssues)}
						<Divider
							orientation="vertical"
							variant="middle"
							flexItem
							className={styles['count-divider']}
						/>
						{displayIssueCounts(
							entityAssociatedData.ASSOCIATED_PRODUCTS,
							associatedProducts,
						)}
						<Divider
							orientation="vertical"
							variant="middle"
							flexItem
							className={styles['count-divider']}
						/>
						{displayIssueCounts(entityAssociatedData.OPEN_TASKS, openTask)}
					</Stack>
				</Grid>
			)}
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				paddingTop={!isTaskCountViewEnabled ? '1rem' : '0rem'}
			>
				<TabContext value={tabIndex}>
					<Box className={styles['tab-container']}>
						<TabList
							onChange={handleChange}
							aria-label="lab API tabs example"
							data-testid="tab-list"
							TabIndicatorProps={{ style: { visibility: 'hidden' } }}
							sx={style}
						>
							<Tab
								label="Overview"
								value="overview"
								className={styles.tab}
								disableRipple
							/>
							<Tab
								label="Impact"
								value="impact"
								className={styles.tab}
								disabled={isImpactHidden()}
								disableRipple
							/>
							<Tab
								label="Tasks"
								value="tasks"
								className={styles.tab}
								disabled={entityType !== CITATION}
								disableRipple
							/>
							{showActivityTab && (
								<Tab
									label="Activity"
									value="activity"
									className={styles.tab}
									disableRipple
								/>
							)}
						</TabList>
					</Box>
					<Box className={styles['controllayout-tab-content']}>
						<TabPanel value="overview">
							<OverviewLayout
								isScrollNeeded={isScrollNeeded}
								entityData={entityData}
								isDeleteEnabled={isDeleteEnabled}
								isLoading={isLoading}
								fetchEntityData={fetchEntityData}
								validationsData={controlValidationData}
								controlValidationDataLoading={isControlValidationDataLoading}
								pageModule={
									moduleType === REQUIREMENT_INVENTORY ||
									moduleType === RISK_REFERENCE_TAXONOMY
										? moduleType
										: HORIZON_SCAN_AND_ALERTS
								}
							/>
						</TabPanel>
						<TabPanel value="impact">
							<Impact
								pagemodule={
									moduleType === REQUIREMENT_INVENTORY ||
									moduleType === RISK_REFERENCE_TAXONOMY
										? moduleType
										: HORIZON_SCAN_AND_ALERTS
								}
								subModuleType={subModuleType}
								isDeleteEnabled={isDeleteEnabled}
							/>
						</TabPanel>
						<TabPanel value="tasks">
							<Tasks
								data={entityData}
								objectType={type}
								objectId={id}
								validationData={controlValidationData}
								readOnly={readOnly}
								moduleType={moduleType}
							/>
						</TabPanel>
						<TabPanel value="activity">Activity</TabPanel>
					</Box>
				</TabContext>
			</Grid>
		</Grid>
	);
};

export default DetailedMappingLayout;
